import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, BellDot, Home, Menu, X, CheckCheck, Trash2, LayoutGrid, Compass, Map, Calendar, Users2, Sparkles, Settings, HelpCircle, Scale, PiggyBank, Plane, ArrowRight, Shield, MessageSquareHeart } from 'lucide-react';
import Sidebar from './Sidebar';
import ChatBot from './ChatBot';
import NotificationItem from './NotificationItem';
import tripsApi from '../api/tripsApi';

// ─── Searchable Items Registry ──────────────────────────────────────────────
const SEARCH_ITEMS = [
  // Pages (sidebar nav items)
  { id: 'dashboard', label: 'Dashboard', description: 'Overview, stats & analytics', path: '/dashboard', icon: LayoutGrid, category: 'Pages', keywords: ['home', 'overview', 'main', 'stats', 'analytics', 'dashboard'] },
  { id: 'explore-trips', label: 'Explore Trips', description: 'Discover new destinations', path: '/dashboard/explore-trips', icon: Compass, category: 'Pages', keywords: ['explore', 'trips', 'destinations', 'discover', 'travel', 'places', 'find trips'] },
  { id: 'my-trips', label: 'My Trips', description: 'View & manage your trips', path: '/dashboard/my-trips', icon: Map, category: 'Pages', keywords: ['my trips', 'trips', 'planned', 'booked', 'upcoming', 'travel plans'] },
  { id: 'trip-feedback', label: 'Trip Feedback', description: 'Expedition reviews & companion testimonials', path: '/dashboard/feedback', icon: MessageSquareHeart, category: 'Pages', keywords: ['feedback', 'reviews', 'ratings', 'companions', 'testimonials', 'ratings', 'shoutouts'] },
  { id: 'my-calendar', label: 'My Calendar', description: 'View & sync your calendar events', path: '/dashboard/my-calendar', icon: Calendar, category: 'Pages', keywords: ['calendar', 'events', 'schedule', 'my calendar', 'sync', 'google', 'holidays'] },
  { id: 'travel-buddies', label: 'Travel Buddies', description: 'Find & connect with travelers', path: '/dashboard/travel-buddies', icon: Users2, category: 'Pages', keywords: ['buddies', 'friends', 'travel', 'connect', 'people', 'companions', 'group'] },
  { id: 'ai-planner', label: 'AI Planner', description: 'Smart budget planning with AI', path: '/dashboard/ai-planner', icon: Sparkles, category: 'Pages', keywords: ['ai', 'planner', 'budget', 'smart', 'plan', 'money', 'expense', 'cost'] },
  { id: 'account-settings', label: 'Account Settings', description: 'Manage your profile & preferences', path: '/dashboard/account-settings', icon: Settings, category: 'Pages', keywords: ['account', 'settings', 'profile', 'preferences', 'edit', 'password', 'email', 'name'] },
  { id: 'terms-policy', label: 'Terms & Policy', description: 'Terms of service & privacy', path: '/dashboard/terms-and-policy', icon: Scale, category: 'Pages', keywords: ['terms', 'policy', 'privacy', 'legal', 'conditions', 'rules'] },
  { id: 'help-support', label: 'Help & Support', description: 'FAQ, contact & assistance', path: '/dashboard/help-support', icon: HelpCircle, category: 'Pages', keywords: ['help', 'support', 'faq', 'contact', 'assistance', 'question', 'issue', 'problem'] },
  // Quick Actions
  { id: 'create-trip', label: 'Create New Trip', description: 'Plan a new adventure', path: '/dashboard/create-trip', icon: Plane, category: 'Actions', keywords: ['create', 'new', 'trip', 'plan', 'add', 'start', 'adventure'] },
  { id: 'find-buddies', label: 'Find Travel Buddies', description: 'Connect with fellow travelers', path: '/dashboard/travel-buddies', icon: Users2, category: 'Actions', keywords: ['find', 'buddies', 'search', 'connect', 'travelers'] },
  { id: 'sync-calendar', label: 'Sync Google Calendar', description: 'Import your events & holidays', path: '/dashboard/my-calendar', icon: Calendar, category: 'Actions', keywords: ['sync', 'google', 'calendar', 'import', 'connect'] },
  { id: 'budget-plan', label: 'Plan Budget with AI', description: 'Get AI-powered trip budget', path: '/dashboard/ai-planner', icon: PiggyBank, category: 'Actions', keywords: ['budget', 'plan', 'ai', 'cost', 'expense', 'money'] },
];

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Real-Time Notifications State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await tripsApi.getNotifications();
      if (data) {
        setNotifications(data.notifications || []);
        setUnreadCount(
          typeof data.unreadCount === 'number'
            ? data.unreadCount
            : (data.notifications || []).filter((n) => !n.isRead).length
        );
      }
    } catch (err) {
      console.warn('Failed to fetch real-time notifications:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000); // 15s refresh
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await tripsApi.markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id || n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await tripsApi.markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await tripsApi.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id && n._id !== id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const clearAll = async () => {
    try {
      await tripsApi.clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    const notifId = notification.id || notification._id;
    markAsRead(notifId);
    setShowNotifications(false);
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.relatedId) {
      navigate(`/dashboard/trip/${notification.relatedId}`);
    }
  };

  const acceptBuddyRequest = (id) => {
    deleteNotification(id);
  };

  const rejectBuddyRequest = (id) => {
    deleteNotification(id);
  };

  // ─── Search State ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  // ─── Search Logic ───────────────────────────────────────────────────────────
  const getSearchResults = useCallback((query) => {
    if (!query.trim()) return [];
    const term = query.toLowerCase().trim();
    
    return SEARCH_ITEMS.map(item => {
      let score = 0;
      const label = item.label.toLowerCase();
      const desc = item.description.toLowerCase();
      const keywords = item.keywords || [];

      if (label === term) score += 100;
      else if (label.startsWith(term)) score += 80;
      else if (label.includes(term)) score += 60;
      else if (desc.includes(term)) score += 40;
      
      const keywordMatch = keywords.some(k => k.includes(term));
      if (keywordMatch) score += 30;

      return { ...item, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);
  }, []);

  useEffect(() => {
    const results = getSearchResults(searchQuery);
    setSearchResults(results);
    setHighlightedIndex(-1);
  }, [searchQuery, getSearchResults]);

  // ─── Click outside to close ───────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Keyboard Navigation ─────────────────────────────────────────────
  const handleSearchKeyDown = (e) => {
    const hasResults = searchQuery.trim().length > 0 && searchResults.length > 0;
    if (!hasResults) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : searchResults.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        handleResultClick(searchResults[highlightedIndex]);
      } else if (searchResults.length > 0) {
        handleResultClick(searchResults[0]);
      }
    } else if (e.key === 'Escape') {
      setSearchFocused(false);
      setShowMobileSearch(false);
      setSearchQuery('');
    }
  };

  const handleResultClick = (item) => {
    navigate(item.path);
    setSearchQuery('');
    setSearchFocused(false);
    setShowMobileSearch(false);
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <span key={i} className="bg-yellow-200 text-gray-900 rounded-sm px-0.5">{part}</span> 
        : part
    );
  };

  const groupedResults = searchResults.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Cleanup on unmount to ensure body.overflow is restored
  useEffect(() => {
    return () => {
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
    };
  }, []);

  const toggleMobileMenu = () => {
    const newOpenState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newOpenState);
    if (newOpenState) {
      document.body.classList.add('mobile-menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove('mobile-menu-open');
    document.body.style.overflow = '';
  };

  const getChildRouteName = () => {
    try {
      const parts = location.pathname.split('/').filter(Boolean);
      const dashboardIndex = parts.findIndex(p => p.toLowerCase() === 'dashboard');
      const child = dashboardIndex >= 0 && parts.length > dashboardIndex + 1 ? parts[dashboardIndex + 1] : null;
      if (!child) return null;

      if (child === 'ai-planner') {
        return 'AI Planner';
      }

      return child
        .replace(/[-_]+/g, ' ')
        .split(' ')
        .map(s => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
    } catch (e) {
      return null;
    }
  };

  const childRouteName = getChildRouteName();

  const handleClearAll = () => {
    clearAll();
    setShowNotifications(false);
  };

  return (
    <>
      <div className="flex min-h-screen max-h-screen overflow-hidden" style={{ background: 'linear-gradient(180deg, #7FE1D7 0%, #97E7DE 100%)' }}>
        {/* Desktop Sidebar */}
        <div className="hidden lg:block py-5 pl-5 flex-shrink-0">
          <Sidebar />
        </div>

        {/* Main Content */}
        <div className={`flex-1 py-3 px-2 md:py-5 md:px-3 min-w-0 overflow-hidden transition-all duration-300 ${showNotifications ? 'sm:mr-96' : ''}`} style={{ position: 'relative' }}>
          <div className="w-full h-full rounded-[40px] bg-white flex flex-col overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
            <div className="flex-shrink-0 flex justify-between items-center px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b bg-white z-10">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-between lg:justify-center w-full">
                  {/* LEFT: LOGO */}
                  <img
                    src="/images/sync_logo.png"
                    alt="Logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    className="h-10 sm:h-12 w-auto object-contain mr-4 cursor-pointer"
                    onClick={() => navigate('/')}
                  />

                  {/* CENTER: CHILD ROUTE NAME */}
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 text-center">
                    {childRouteName ? (
                      <span className="text-gray-900 font-medium">{childRouteName}</span>
                    ) : null}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-4">
                {/* Desktop Search */}
                <div className="relative hidden sm:block" ref={searchRef}>
                  <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors duration-200 ${searchFocused ? 'text-[#2A9D8F]' : 'text-gray-400'}`} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search pages, actions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-9 pr-8 py-2 text-sm rounded-full border border-gray-200 focus:outline-none focus:border-[#2A9D8F] focus:ring-4 focus:ring-[#2A9D8F]/10 w-[220px] lg:w-[280px] transition-all duration-300"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Search Dropdown */}
                  {searchFocused && searchQuery.trim() !== '' && (
                    <div className="absolute top-full left-0 mt-2 w-[320px] lg:w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-searchDropdown">
                      <div className="max-h-[450px] overflow-y-auto search-dropdown-scroll">
                        {searchResults.length > 0 ? (
                          Object.entries(groupedResults).map(([category, items]) => (
                            <div key={category} className="py-2">
                              <div className="px-4 py-1 flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{category}</span>
                                <div className="flex-1 h-px bg-gray-50"></div>
                              </div>
                              {items.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                const globalIndex = searchResults.indexOf(item);
                                return (
                                  <button
                                    key={item.id}
                                    onClick={() => handleResultClick(item)}
                                    onMouseEnter={() => setHighlightedIndex(globalIndex)}
                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 ${
                                      highlightedIndex === globalIndex ? 'bg-[#2A9D8F]/5 border-r-4 border-[#2A9D8F]' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      isActive ? 'bg-[#2A9D8F]/10 text-[#2A9D8F]' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                      <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <p className={`text-sm font-semibold truncate ${isActive ? 'text-[#2A9D8F]' : 'text-gray-800'}`}>
                                          {highlightMatch(item.label, searchQuery)}
                                        </p>
                                        {isActive && <span className="text-[10px] font-medium text-[#2A9D8F] bg-[#2A9D8F]/10 px-1.5 py-0.5 rounded-md">Current</span>}
                                      </div>
                                      <p className="text-[11px] text-gray-400 truncate">{highlightMatch(item.description, searchQuery)}</p>
                                    </div>
                                    <ArrowRight className={`w-3.5 h-3.5 text-gray-300 transition-transform duration-200 ${highlightedIndex === globalIndex ? 'translate-x-0.5 text-[#2A9D8F]' : ''}`} />
                                  </button>
                                );
                              })}
                            </div>
                          ))
                        ) : (
                          <div className="px-6 py-10 text-center">
                            <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                            <p className="text-sm text-gray-500 font-medium">No results for "{searchQuery}"</p>
                            <p className="text-xs text-gray-400 mt-1 italic">Try searching for "trips", "planner", or "buddies"</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Footer hints */}
                      {searchResults.length > 0 && (
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
                          <div className="flex gap-3">
                            <span className="flex items-center gap-1"><span className="bg-white border rounded px-1 group-hover:bg-gray-100">↑↓</span> to navigate</span>
                            <span className="flex items-center gap-1"><span className="bg-white border rounded px-1">↵</span> to select</span>
                          </div>
                          <span>{searchResults.length} results</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile Search Button */}
                <button 
                  onClick={() => { setShowMobileSearch(true); setSearchQuery(''); }}
                  className="sm:hidden w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-200 hover:border-[#2A9D8F] hover:shadow-md transition-all duration-200"
                >
                  <Search className="w-5 h-5 text-gray-600" />
                </button>

                {/* Modern Notification Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowNotifications(!showNotifications);
                      if (!showNotifications) fetchNotifications();
                    }}
                    className="relative w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-200 hover:border-[#2A9D8F] hover:shadow-md transition-all duration-200"
                  >
                    {unreadCount > 0 ? (
                      <BellDot className="w-5 h-5 text-[#2A9D8F]" />
                    ) : (
                      <Bell className="w-5 h-5 text-gray-600" />
                    )}
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <>
                      {/* Full screen backdrop for mobile */}
                      <div
                        className="sm:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998]"
                        onClick={() => setShowNotifications(false)}
                      ></div>

                      {/* Responsive Dropdown */}
                      <div className="sm:block fixed top-0 right-0 w-96 h-screen bg-white shadow-2xl border-l border-gray-100 z-[9999] flex flex-col min-h-0 pointer-events-auto overflow-y-auto overflow-x-hidden notifications-scroll">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#2A9D8F] to-[#238177] p-4 sm:p-4 flex-shrink-0 sticky top-0 z-10">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="text-white font-bold text-lg">Notifications</h3>
                              <p className="text-white/90 text-xs sm:text-sm">
                                {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up! 🎉'}
                              </p>
                            </div>
                            <button
                              onClick={() => setShowNotifications(false)}
                              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full active:scale-95"
                              aria-label="Close notifications"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Action Buttons */}
                          {(unreadCount > 0 || notifications.length > 0) && (
                            <div className="flex gap-2">
                              {unreadCount > 0 && (
                                <button
                                  onClick={markAllAsRead}
                                  className="flex-1 bg-white/20 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm text-white text-xs sm:text-sm py-2.5 sm:py-2 px-3 rounded-xl sm:rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium active:scale-95"
                                >
                                  <CheckCheck className="w-4 h-4" />
                                  <span>Mark all read</span>
                                </button>
                              )}
                              {notifications.length > 0 && (
                                <button
                                  onClick={handleClearAll}
                                  className="flex-shrink-0 bg-white/20 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm text-white text-xs sm:text-sm py-2.5 sm:py-2 px-4 sm:px-3 rounded-xl sm:rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium active:scale-95"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Clear all</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Notifications List - Scrollable */}
                        <div className="flex-1 min-h-0">
                          {notifications.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center justify-center min-h-[200px]">
                              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Bell className="w-10 h-10 text-teal-600" />
                              </div>
                              <p className="text-gray-700 font-semibold text-base mb-1">No notifications</p>
                              <p className="text-gray-500 text-sm">You're all caught up!</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {notifications.map((notification) => (
                                <NotificationItem
                                  key={notification.id || notification._id}
                                  notification={notification}
                                  onMarkAsRead={markAsRead}
                                  onDelete={deleteNotification}
                                  onAccept={acceptBuddyRequest}
                                  onReject={rejectBuddyRequest}
                                  onClickItem={handleNotificationClick}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Mobile Notification Panel */}
                      <div className="sm:hidden fixed left-0 right-0 bottom-0 w-full bg-white rounded-t-3xl shadow-2xl border-t border-gray-100 z-[9999] animate-slideUp max-h-[85vh] flex flex-col min-h-0 overflow-y-auto overflow-x-hidden notifications-scroll overscroll-contain">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#2A9D8F] to-[#238177] p-4 flex-shrink-0 sticky top-0 z-10">
                          <div className="w-12 h-1 bg-white/30 rounded-full mx-auto mb-3"></div>

                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="text-white font-bold text-lg">Notifications</h3>
                              <p className="text-white/90 text-xs">
                                {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up! 🎉'}
                              </p>
                            </div>
                            <button
                              onClick={() => setShowNotifications(false)}
                              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full active:scale-95"
                              aria-label="Close notifications"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>

                          {(unreadCount > 0 || notifications.length > 0) && (
                            <div className="flex gap-2">
                              {unreadCount > 0 && (
                                <button
                                  onClick={markAllAsRead}
                                  className="flex-1 bg-white/20 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm text-white text-xs py-2.5 px-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-medium active:scale-95"
                                >
                                  <CheckCheck className="w-4 h-4" />
                                  <span>Mark all read</span>
                                </button>
                              )}
                              {notifications.length > 0 && (
                                <button
                                  onClick={handleClearAll}
                                  className="flex-shrink-0 bg-white/20 hover:bg-white/30 active:bg-white/40 backdrop-blur-sm text-white text-xs py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-medium active:scale-95"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Clear</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Notifications List - Scrollable */}
                        <div className="flex-1 min-h-0">
                          {notifications.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center justify-center min-h-[200px]">
                              <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Bell className="w-10 h-10 text-teal-600" />
                              </div>
                              <p className="text-gray-700 font-semibold text-base mb-1">No notifications</p>
                              <p className="text-gray-500 text-sm">You're all caught up!</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {notifications.map((notification) => (
                                <NotificationItem
                                  key={notification.id || notification._id}
                                  notification={notification}
                                  onMarkAsRead={markAsRead}
                                  onDelete={deleteNotification}
                                  onAccept={acceptBuddyRequest}
                                  onReject={rejectBuddyRequest}
                                  onClickItem={handleNotificationClick}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Footer - Mobile Close Button */}
                        <div className="p-3 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                          <button
                            onClick={() => setShowNotifications(false)}
                            className="w-full bg-[#2A9D8F] hover:bg-[#238177] active:bg-[#1f7265] text-white font-semibold py-3 rounded-xl transition-all duration-200 active:scale-95"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Admin Portal Shortcut if Admin */}
                {(localStorage.getItem('userRole') === 'admin' || localStorage.getItem('userRole') === 'superAdmin') && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full bg-gradient-to-r from-teal-700 to-emerald-700 text-white font-bold hover:shadow-lg transition-all"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-semibold hidden lg:inline">Admin Portal</span>
                  </button>
                )}

                {/* Back to Home - Responsive */}
                <button
                  onClick={() => navigate('/')}
                  className="hidden sm:flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full bg-[#2A9D8F] text-white hover:bg-[#238177] transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span className="text-sm font-medium hidden lg:inline">Back to Home</span>
                </button>

                {/* Mobile & Tablet Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="lg:hidden w-10 h-10 rounded-full mr-1 flex items-center justify-center bg-white border border-gray-200 hover:border-[#2A9D8F] hover:shadow-md transition-all duration-200 cursor-pointer"
                  aria-label="Toggle Menu"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto" style={{ pointerEvents: isMobileMenuOpen ? 'none' : 'auto' }}>
              {children || <Outlet />}
            </div>

            {/* Mobile Sidebar Overlay */}
            {isMobileMenuOpen && (
              <div className="fixed inset-0 z-50 lg:hidden" style={{ isolation: 'isolate' }}>
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-40"
                  onClick={closeMobileMenu}
                  style={{ touchAction: 'none' }}
                ></div>
                <div className="fixed left-0 top-0 h-[100dvh] w-[280px] max-w-[80vw] bg-white rounded-r-[40px] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col z-50 overflow-hidden">
                  <div className="p-4 flex-shrink-0">
                    <button
                      onClick={closeMobileMenu}
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <Sidebar onClose={closeMobileMenu} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ChatBot isDraggable={location.pathname.includes('/travel-buddies')} />

      {/* Mobile Fullscreen Search Overlay */}
      {showMobileSearch && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col animate-searchOpen">
          <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100 bg-gradient-to-r from-[#2A9D8F] to-[#238177]">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
              <input
                ref={mobileSearchInputRef}
                type="text"
                placeholder="Search pages, actions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                className="w-full pl-9 pr-8 py-2.5 text-sm rounded-full bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:bg-white/30 focus:border-white/50"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); mobileSearchInputRef.current?.focus(); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={() => { setShowMobileSearch(false); setSearchQuery(''); }}
              className="text-white font-medium text-sm px-2 py-1"
            >
              Cancel
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {searchQuery.trim() === '' ? (
              <div className="px-5 py-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Quick Navigation</p>
                <div className="grid grid-cols-2 gap-3">
                  {SEARCH_ITEMS.filter(i => i.category === 'Pages').slice(0, 6).map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleResultClick(item)}
                        className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-xl text-left hover:bg-[#2A9D8F]/8 transition-colors border border-gray-100 active:scale-95 duration-200"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-[#2A9D8F]" />
                        </div>
                        <span className="text-xs font-medium text-gray-700 truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="py-2">
                {Object.entries(groupedResults).map(([category, items]) => (
                  <div key={category}>
                    <div className="px-5 py-2 flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{category}</span>
                      <div className="flex-1 h-px bg-gray-100"></div>
                    </div>
                    {items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleResultClick(item)}
                          className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            isActive ? 'bg-[#2A9D8F]/15 text-[#2A9D8F]' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{highlightMatch(item.label, searchQuery)}</p>
                            <p className="text-xs text-gray-400 truncate">{highlightMatch(item.description, searchQuery)}</p>
                          </div>
                          {isActive && <span className="text-[10px] font-medium text-[#2A9D8F] bg-[#2A9D8F]/10 px-2 py-0.5 rounded-full">Current</span>}
                          <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-5 py-12 text-center">
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No results for "{searchQuery}"</p>
                <p className="text-xs text-gray-400 mt-1">Try "trips", "calendar", "buddies", or "settings"</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes searchDropdown {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes searchOpen {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-slideDown {
    animation: slideDown 0.3s ease-out;
  }

  .animate-searchDropdown {
    animation: searchDropdown 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .animate-searchOpen {
    animation: searchOpen 0.25s ease-out;
  }

  /* Search Dropdown Scrollbar */
  .search-dropdown-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(42, 157, 143, 0.25) transparent;
  }

  .search-dropdown-scroll::-webkit-scrollbar {
    width: 4px;
  }

  .search-dropdown-scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  .search-dropdown-scroll::-webkit-scrollbar-thumb {
    background: rgba(42, 157, 143, 0.25);
    border-radius: 999px;
  }

  .search-dropdown-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(42, 157, 143, 0.4);
  }

  @keyframes slideRight {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .animate-slideRight {
    animation: slideRight 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
`}</style>
    </>
  );
};

export default DashboardLayout;
